import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Cube, Lightning, Target, Sparkle } from "@phosphor-icons/react";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import Reveal from "@/components/site/Reveal";
import CountUp from "@/components/site/CountUp";
import FloatingShapes from "@/components/site/FloatingShapes";

const TECHNOLOGIES = [
  "REACT", "NEXT.JS", "REACT NATIVE", "NODE.JS", "TYPESCRIPT", "PYTHON",
  "AWS", "GCP", "AZURE", "KUBERNETES", "DOCKER", "TERRAFORM",
  "POSTGRESQL", "GRAPHQL", "FASTAPI", "FIGMA",
];

const STATS = [
  { value: "120+", label: "Products Shipped" },
  { value: "38", label: "Engineers" },
  { value: "11", label: "Countries Served" },
  { value: "99.98%", label: "Avg. Uptime" },
];

export default function Home() {
  const shouldReduceMotion = useReducedMotion();

  const heroRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20, mass: 0.5 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20, mass: 0.5 });
  const cube1X = useTransform(springX, [-0.5, 0.5], [-18, 18]);
  const cube1Y = useTransform(springY, [-0.5, 0.5], [-18, 18]);
  const cube2X = useTransform(springX, [-0.5, 0.5], [16, -16]);
  const cube2Y = useTransform(springY, [-0.5, 0.5], [16, -16]);
  const dotsX = useTransform(springX, [-0.5, 0.5], [-10, 10]);
  const dotsY = useTransform(springY, [-0.5, 0.5], [-10, 10]);

  const handleHeroMouseMove = (e) => {
    if (shouldReduceMotion || !heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleHeroMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={handleHeroMouseLeave}
        className="relative overflow-hidden border-b border-black/10"
      >
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        {!shouldReduceMotion && (
          <motion.span
            aria-hidden
            className="hidden md:block absolute -right-16 top-16 w-72 h-72 border-[3px] border-[#FF4F00]/20 pointer-events-none"
            style={{ x: cube1X, y: cube1Y }}
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          />
        )}
        {!shouldReduceMotion && (
          <motion.span
            aria-hidden
            className="hidden md:block absolute -right-4 top-32 w-40 h-40 border-2 border-[#0A0A0A]/10 pointer-events-none"
            style={{ x: cube2X, y: cube2Y }}
            animate={{ rotate: -360 }}
            transition={{ duration: 34, repeat: Infinity, ease: "linear" }}
          />
        )}
        {!shouldReduceMotion && <FloatingShapes style={{ x: dotsX, y: dotsY }} />}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00] reveal-up">
                / UCUBE — Engineering Studio
              </p>
              <h1 className="font-display mt-6 text-mega font-black tracking-tighter uppercase leading-[0.88] reveal-up" style={{animationDelay: '.05s'}}>
                Engineering<br/>the future,<br/>
                <span className="inline-flex items-baseline gap-3">
                  <span>cube</span>
                  <span className="text-[#FF4F00]">by</span>
                  <span>cube.</span>
                </span>
              </h1>
            </div>
            <div className="col-span-12 md:col-span-4">
              <p className="text-base text-[#52525B] leading-relaxed max-w-sm reveal-up" style={{animationDelay: '.15s'}}>
                We are an IT services studio for ambitious teams. From product engineering to AI, cloud, and security — we build structural software that lasts.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 reveal-up" style={{animationDelay: '.2s'}}>
                <Link to="/contact" data-testid="hero-cta-primary" className="bg-[#FF4F00] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E64700] inline-flex items-center gap-2">
                  Start a Project <ArrowRight size={16} weight="bold" />
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary" className="border-2 border-[#0A0A0A] px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#0A0A0A] hover:text-white inline-flex items-center gap-2">
                  Our Services
                </Link>
              </div>
            </div>
          </div>

          {/* meta strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-b border-black/10 divide-x divide-black/10">
            {STATS.map((s, idx) => (
              <Reveal key={s.label} delay={idx * 0.08} className="px-6 py-6">
                <p className="font-display text-3xl md:text-4xl font-extrabold"><CountUp value={s.value} /></p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#52525B]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECH MARQUEE */}
      <section className="border-b border-black/10 py-8 overflow-hidden">
        <div className="flex items-center gap-12 animate-marquee whitespace-nowrap">
          {[...TECHNOLOGIES, ...TECHNOLOGIES].map((n, i) => (
            <span key={i} className="font-mono text-sm uppercase tracking-[0.3em] text-[#0A0A0A]/60">
              ◆ {n}
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <div className="col-span-12 md:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ 02 — Capabilities</p>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">
                Six disciplines.<br/>One structural team.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-[#52525B] text-base leading-relaxed">
                We work as a single multi-disciplinary studio — engineers, designers, and strategists shipping under one roof.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = s.icon;
              const span = idx === 0 ? "md:col-span-7" : idx === 1 ? "md:col-span-5" : idx === 2 ? "md:col-span-5" : idx === 3 ? "md:col-span-7" : idx === 4 ? "md:col-span-6" : "md:col-span-6";
              const dark = idx === 0 || idx === 3;
              return (
                <Reveal key={s.id} delay={(idx % 3) * 0.1} className={span}>
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.id}`}
                    className={`group relative block h-full border border-black/10 p-8 md:p-10 hover:border-black hover:-translate-y-1 hover:shadow-brutal transition-all ${dark ? "bg-[#0A0A0A] text-white hover:border-[#FF4F00] hover:shadow-brutal-orange" : "bg-white"}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 inline-flex items-center justify-center border ${dark ? "border-white/20" : "border-black/20"} group-hover:bg-[#FF4F00] group-hover:border-[#FF4F00] group-hover:text-white transition-colors`}>
                        <Icon size={22} weight="duotone" />
                      </div>
                      <ArrowUpRight size={22} weight="bold" className="opacity-50 group-hover:opacity-100 group-hover:text-[#FF4F00] group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
                    </div>
                    <h3 className="font-display mt-8 text-2xl md:text-3xl font-bold tracking-tight uppercase">{s.title}</h3>
                    <p className={`mt-3 text-sm leading-relaxed max-w-md ${dark ? "text-white/70" : "text-[#52525B]"}`}>{s.short}</p>
                    <p className={`mt-8 text-[11px] font-bold uppercase tracking-[0.25em] ${dark ? "text-white/40" : "text-[#0A0A0A]/40"}`}>0{idx + 1} / 06</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

    {/* Industries */}
    <section className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ 02 — Capabilities</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">
              Six disciplines.<br/>One structural team.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-[#52525B] text-base leading-relaxed">
              We work as a single multi-disciplinary studio — engineers, designers, and strategists shipping under one roof.
            </p>
          </div>
        </div>
      </div>
    </section>

      {/* PROCESS */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <div className="col-span-12 md:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ 03 — Method</p>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">
                A method built on clarity, cadence, and craft.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-black/10">
            {[
              { n: "01", t: "Diagnose", d: "We start with a hard look at goals, constraints, and risks. No fluff, just the brief.", icon: Target },
              { n: "02", t: "Design", d: "Architecture, interface, and delivery plan — sized to your stage and runway.", icon: Cube },
              { n: "03", t: "Deliver", d: "Senior squads ship weekly. Observable, secure, and measured against business KPIs.", icon: Lightning },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.n} delay={idx * 0.12} className="border-b md:border-b-0 md:border-r last:border-r-0 border-black/10 p-8 md:p-10">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-5xl font-black text-[#FF4F00]">{step.n}</span>
                    <Icon size={28} weight="duotone" />
                  </div>
                  <h3 className="font-display mt-8 text-2xl font-bold uppercase tracking-tight">{step.t}</h3>
                  <p className="mt-3 text-[#52525B] text-sm leading-relaxed">{step.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-b border-black/10 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <span className="font-display text-[120px] leading-none text-[#FF4F00]" aria-hidden>“</span>
          </div>
          <Reveal as="div" className="col-span-12 md:col-span-10">
            <p className="font-display text-2xl md:text-4xl font-medium leading-[1.2] tracking-tight max-w-4xl">
              UCUBE rebuilt our platform in 14 weeks. The team operated like a senior internal squad — fewer meetings, more shipped product, and measurable lifts across activation and retention.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white inline-flex items-center justify-center font-display font-extrabold">A</div>
              <div>
                <p className="font-bold text-sm">Aanya Mehta</p>
                <p className="text-xs text-[#52525B] uppercase tracking-[0.2em]">Head of Product, Northwind</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 relative">
          <div className="grid grid-cols-12 gap-6 items-end">
            <Reveal as="div" className="col-span-12 md:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ Next Step</p>
              <h2 className="font-display mt-4 text-mega font-black tracking-tighter uppercase leading-[0.9]">
                Got a hard<br/>problem?{" "}
                <motion.span
                  className="inline-block -translate-y-2"
                  animate={shouldReduceMotion ? undefined : { rotate: [0, -12, 12, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" }}
                >
                  <Sparkle size={48} weight="fill" className="inline text-[#FF4F00]" />
                </motion.span>
              </h2>
            </Reveal>
            <Reveal as="div" delay={0.1} className="col-span-12 md:col-span-4">
              <p className="text-white/70 max-w-sm">
                Tell us about your roadmap, your team, and what's keeping you up at night. We respond inside 24 hours.
              </p>
              <Link to="/contact" data-testid="home-cta-bottom-button" className="mt-8 inline-flex items-center gap-3 bg-[#FF4F00] text-white px-8 py-5 text-sm font-bold uppercase tracking-[0.2em] hover:bg-[#E64700]">
                Book a Call <ArrowRight size={18} weight="bold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
